// Spiellogik als reine Funktionen: jeder Schritt liefert einen neuen Zustand.
import { baselineSources, summarize } from "./baseline.js"
import { rng, pick } from "./rng.js"

const QUALITY_UNCERTAINTY = { A: 0.1, B: 0.2, C: 0.3, D: 0.5 }
const PURCHASE_STATIONS = ["steel_mill", "chem_plant", "component_works", "farm", "quarry", "office_park"]

export function newGame(data, { industry, employees, seed = Date.now() }) {
  const random = rng(seed)
  const events = data.events.pools.map((pool) => pick(random, pool))
  const base = baselineSources(data, industry, employees)
  return {
    industry,
    employees,
    seed,
    guess: null,
    round: 0,
    budget: data.actions.budget_per_round,
    spent: 0,
    owned: {},
    events,
    eventResults: [],
    finished: false,
    baseSources: base
  }
}

export function setGuess(state, percent) {
  return { ...state, guess: Math.max(0, Math.min(100, Math.round(percent))) }
}

// Aktionen, die für diese Branche eine spürbare Wirkung haben. Maßnahmen am eigenen
// Werk zählen relativ zu Scope 1 + 2 (ab 3 %), Scope-3-Maßnahmen relativ zur Gesamtbilanz (ab 1 %).
export function applicableActions(data, state) {
  const base = summarize(data, state.baseSources)
  const relevant = (action) => (action.scope === "3"
    ? targetedShare(action, base) >= 0.01
    : targetedTonnes(action, base) / base.scope12 >= 0.03)
  const hasScope3Levers = Object.values(data.actions.actions).some((action) => action.scope === "3" && relevant(action))
  return Object.entries(data.actions.actions)
    .filter(([, action]) => (action.enables_targeting ? hasScope3Levers : relevant(action)))
    .map(([id]) => id)
}

// Text „vorsorgen mit …“ aus den Maßnahmen, die es in dieser Branche tatsächlich gibt.
export function eventRemedies(data, state, event) {
  const available = new Set(applicableActions(data, state))
  return event.requires_any.filter((id) => available.has(id)).map((id) => data.actions.actions[id].label)
}

function targetedTonnes(action, summary) {
  return Object.keys(action.targets).reduce((sum, key) => sum + (summary.sources[key] || 0), 0)
}

function targetedShare(action, summary) {
  return targetedTonnes(action, summary) / summary.total
}

export function isTargeted(state, round = state.round) {
  const accountingRound = state.owned.carbon_accounting
  return accountingRound !== undefined && accountingRound <= round
}

export function efficacy(data, state, actionId, round = state.round) {
  const action = data.actions.actions[actionId]
  if (action.scope !== "3" || action.enables_targeting) return 1
  return isTargeted(state, round) ? 1 : data.actions.untargeted_efficacy
}

export function canBuy(data, state, actionId) {
  const action = data.actions.actions[actionId]
  if (!action || state.finished) return false
  if (state.owned[actionId] !== undefined) return false
  return action.cost <= state.budget
}

export function buyAction(data, state, actionId) {
  if (!canBuy(data, state, actionId)) throw new Error(`Aktion nicht möglich: ${actionId}`)
  const cost = data.actions.actions[actionId].cost
  return {
    ...state,
    budget: state.budget - cost,
    spent: state.spent + cost,
    owned: { ...state.owned, [actionId]: state.round }
  }
}

export function eventFor(data, state, round = state.round) {
  const id = state.events[round]
  return { id, ...data.events.events[id] }
}

export function resolveEvent(data, state, round = state.round) {
  const event = eventFor(data, state, round)
  const success = event.requires_any.some((id) => state.owned[id] !== undefined && state.owned[id] <= round)
  const outcome = success ? event.success : event.failure
  return { id: event.id, success, budget: outcome.budget, text: outcome.text }
}

// Beendet die aktuelle Runde: Ereignis auflösen, Budget fortschreiben.
export function endRound(data, state) {
  if (state.finished) return state
  const outcome = resolveEvent(data, state)
  const eventResults = [...state.eventResults, outcome]
  const lastRound = state.round >= data.actions.rounds.length - 1
  if (lastRound) return { ...state, eventResults, finished: true }
  return {
    ...state,
    eventResults,
    round: state.round + 1,
    budget: Math.max(0, state.budget + outcome.budget + data.actions.budget_per_round)
  }
}

// Emissionen nach allen gekauften Aktionen (Zielbild im letzten Rundenjahr).
export function emissions(data, state, owned = state.owned) {
  const sources = { ...state.baseSources }
  for (const [id, round] of Object.entries(owned)) {
    const action = data.actions.actions[id]
    const factor = efficacy(data, { ...state, owned }, id, round)
    for (const [source, reduction] of Object.entries(action.targets)) {
      if (sources[source] !== undefined) sources[source] *= 1 - reduction * factor
    }
  }
  return summarize(data, sources)
}

export function stationQuality(data, state) {
  const quality = {}
  const targeted = state.owned.carbon_accounting !== undefined
  for (const [id, station] of Object.entries(data.stations.stations)) {
    quality[id] = targeted && station.quality === "D" ? "B" : station.quality
  }
  const epd = state.eventResults.find((result) => result.id === "supplier_epd" && result.success)
  if (epd) {
    const base = summarize(data, state.baseSources)
    const biggest = PURCHASE_STATIONS.reduce((best, id) => (base.stations[id] > base.stations[best] ? id : best))
    quality[biggest] = "A"
  }
  return quality
}

export function uncertainty(quality) {
  return QUALITY_UNCERTAINTY[quality] ?? 0.5
}

// Spielt einen Run aus Seed und Käufen nach (für den Offline-QR-Code und zur Kontrolle).
export function replay(data, { industry, employees, seed, guess, actions }) {
  let state = newGame(data, { industry, employees, seed })
  if (guess !== null && guess !== undefined) state = setGuess(state, guess)
  for (let round = 0; round < data.actions.rounds.length; round++) {
    for (const action of actions.filter((a) => a.round === round)) state = buyAction(data, state, action.id)
    state = endRound(data, state)
  }
  return state
}
